/* eslint-disable no-throw-literal */
/**
 * ! This file was generated by swagger-typescript-api. Do not change the file directly.
 */

import { type TransformTypesToCamelCase } from '@credit-web/services/types'
import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { appEnv, CREDIT_API_ENDPOINT, ENABLE_MOCKOON_PROXY } from 'shared/envs'

// eslint-disable-next-line no-restricted-imports
import { type HttpClientInstance, httpClientFactory } from '~/domains/platform/infra/http/http-client-factory'
// eslint-disable-next-line no-restricted-imports
import { keysToSnakecase, transformResponse } from '~/domains/platform/infra/http/http-helpers'
// eslint-disable-next-line no-restricted-imports
import { responseUnauthorizedAccessInterceptor } from '~/domains/platform/infra/http/interceptors/unauthorized-access-interceptor'

import { bffHeadersInterceptor } from './interceptors/bff-headers-interceptor'

export type Result<T, E = unknown> = { ok: true; data: T } | { ok: false; error: E }

export type QueryParamsType = Record<string | number, any>

export enum ContentType {
  Json = 'application/json',
  FormData = 'multipart/form-data',
  UrlEncoded = 'application/x-www-form-urlencoded',
  Text = 'text/plain'
}

export interface FullRequestParams<TBody = unknown>
  extends Omit<AxiosRequestConfig, 'data' | 'params' | 'url' | 'responseType'> {
  /** request url */
  url: string
  /** content type of request body */
  type?: ContentType
  /** query params */
  query?: QueryParamsType
  /** format of response (i.e. response.json() -> format: "json") */
  format?: ResponseType
  /** request body */
  body?: TBody
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'url'>

export interface ApiConfig extends Omit<AxiosRequestConfig, 'data' | 'cancelToken'> {}

function sanitizeLogInput(input: string) {
  return input.replace(/[\n\r\t]/g, '')
}

/**
 * Abstract class to handle API requests in a generic way
 */
export class HttpClient {
  protected http: HttpClientInstance

  constructor(protected basePath: string, { ...axiosConfig }: ApiConfig = {}) {
    const isNotProduction = !appEnv.isProduction()
    const isSurveyPath = basePath.includes('/surveys')
    const enableMockoon = String(ENABLE_MOCKOON_PROXY) === 'true'
    const useMockoonProxy = isNotProduction && isSurveyPath && enableMockoon
    const baseURL = useMockoonProxy ? '/api/mockoon-proxy' : CREDIT_API_ENDPOINT()

    this.http = httpClientFactory({
      baseURL,
      transformResponse: [...(axios.defaults.transformResponse as any[]), transformResponse],
      transformRequest: [keysToSnakecase, ...(axios.defaults.transformRequest as any[])],
      ...axiosConfig,
      requestInterceptor: bffHeadersInterceptor,
      responseErrorInterceptors: [responseUnauthorizedAccessInterceptor]
    })
  }

  /**
   * Get shortcut method
   * @param url
   * @param params
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async get<T = any, _E = any>({
    url,
    query,
    ...params
  }: FullRequestParams): Promise<AxiosResponse<TransformTypesToCamelCase<T>>> {
    try {
      return await this.http<TransformTypesToCamelCase<T>>({
        url: `${this.basePath}${url}`,
        method: 'GET',
        params: query,
        ...params
      })
    } catch (error) {
      const sanitizedUrl = sanitizeLogInput(url)
      console.error('GET %s failed:', sanitizedUrl, error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Get shortcut method without transformed response
   * @param url
   * @param params
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async untransformedGet<T = any, _E = any>({
    url,
    query,
    ...params
  }: FullRequestParams): Promise<AxiosResponse<T>> {
    try {
      return await this.http<T>({
        url: `${this.basePath}${url}`,
        method: 'GET',
        params: query,
        transformResponse: undefined,
        ...params
      })
    } catch (error) {
      console.error('GET %s failed:', url, error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Post shortcut method
   * @param url
   * @param data
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async post<T = any, _E = any, TBody = unknown>({
    url,
    query,
    body,
    ...params
  }: FullRequestParams<TBody>): Promise<AxiosResponse<TransformTypesToCamelCase<T>>> {
    try {
      return await this.http<TransformTypesToCamelCase<T>>({
        url: `${this.basePath}${url}`,
        method: 'POST',
        data: body,
        params: query,
        ...params
      })
    } catch (error) {
      console.error('POST %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Post shortcut method without transformed response
   * @param url
   * @param data
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async untransformedPost<T = any, _E = any, TBody = unknown>({
    url,
    query,
    body,
    ...params
  }: FullRequestParams<TBody>): Promise<AxiosResponse<T>> {
    try {
      return await this.http<T>({
        url: `${this.basePath}${url}`,
        method: 'POST',
        data: body,
        params: query,
        transformResponse: undefined,
        ...params
      })
    } catch (error) {
      console.error('POST %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Patch shortcut method
   * @param url
   * @param data
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async patch<T = any, _E = any, TBody = unknown>({
    url,
    query,
    body,
    ...params
  }: FullRequestParams<TBody>): Promise<AxiosResponse<TransformTypesToCamelCase<T>>> {
    try {
      return await this.http<TransformTypesToCamelCase<T>>({
        url: `${this.basePath}${url}`,
        method: 'PATCH',
        data: body,
        params: query,
        ...params
      })
    } catch (error) {
      console.error('PATCH %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Patch shortcut method without transformed response
   * @param url
   * @param data
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async untransformedPatch<T = any, _E = any, TBody = unknown>({
    url,
    query,
    body,
    ...params
  }: FullRequestParams<TBody>): Promise<AxiosResponse<T>> {
    try {
      return await this.http<T>({
        url: `${this.basePath}${url}`,
        method: 'PATCH',
        data: body,
        params: query,
        transformResponse: undefined,
        ...params
      })
    } catch (error) {
      console.error('PATCH %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Delete shortcut method
   * @param url
   * @param params
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async delete<T = any, _E = any>({
    url,
    query,
    ...params
  }: FullRequestParams): Promise<AxiosResponse<TransformTypesToCamelCase<T>>> {
    try {
      return await this.http<TransformTypesToCamelCase<T>>({
        url: `${this.basePath}${url}`,
        method: 'DELETE',
        params: query,
        ...params
      })
    } catch (error) {
      console.error('DELETE %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }

  /**
   * Delete shortcut method without transformed response
   * @param url
   * @param params
   * @returns
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  public async untransformedDelete<T = any, _E = any>({
    url,
    query,
    ...params
  }: FullRequestParams): Promise<AxiosResponse<T>> {
    try {
      return await this.http<T>({
        url: `${this.basePath}${url}`,
        method: 'DELETE',
        params: query,
        transformResponse: undefined,
        ...params
      })
    } catch (error) {
      console.error('DELETE %s failed:', sanitizeLogInput(url), error)
      if (axios.isAxiosError(error)) {
        throw error as AxiosError<_E>
      }
      throw error
    }
  }
}
